import { NET } from '../config/tuning';

export interface Snapshot {
  serverTime: number;
  x: number;
  z: number;
  facing: number;
}

/**
 * Remote-entity interpolation buffer — design/gdd/movement-netcode.md
 * Formula 7. Remote entities are always rendered INTERP_DELAY_MS in the
 * past, interpolated between two real snapshots, never extrapolated.
 */
export function interpDelayMs(): number {
  return 2 * (1000 / NET.BROADCAST_HZ) + NET.JITTER_BUFFER_MS;
}

/**
 * Shortest signed angular delta from `from` to `to`.
 *
 * User report (2026-08-24): "when I turn, the character itself jerks in
 * different directions." Root cause, and it was here rather than in the
 * camera: `facing` was interpolated with a plain scalar lerp
 * (`before.facing + (after.facing - before.facing) * t`), but the server
 * derives it as `Math.atan2(dx, dz)`, which is discontinuous at the +/-pi
 * boundary — i.e. exactly when you turn to face -Z, "around". Two adjacent
 * snapshots of a character turning a couple of degrees through that boundary
 * read as +3.10 and -3.12 rad, and the lerp then sweeps the *long* way
 * round: a full ~355-degree spin, back and forth, across one interpolation
 * window (~150ms), repeated for as long as the player kept turning near
 * that heading.
 *
 * Every prior fix for "jerky turning" went into the camera controller
 * (`over-shoulder-camera-controller.ts`, three separate amendments), which is
 * why this survived: the camera already used `shortestAngleDelta` for its own
 * yaw, so the *view* was smooth while the *character model* spun — matching
 * the report's wording precisely ("the character itself").
 *
 * Same helper name and semantics as the camera controller's own copy; kept
 * local rather than shared because this module is deliberately dependency-free
 * apart from the tuning table.
 */
function shortestAngleDelta(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export class RemoteInterpolator {
  private buffer: Snapshot[] = [];

  /**
   * Game Feel Polish Phase 5 — this used to prune with
   * `this.buffer = this.buffer.filter(...)`, allocating a whole fresh array
   * on every single snapshot: at BROADCAST_HZ=20 with a dozen tracked
   * entities that's ~260 throwaway arrays/sec, each holding ~40 snapshots,
   * purely to drop the handful that aged out. Snapshots arrive in
   * server-time order, so everything expiring is a contiguous run at the
   * front — one `splice` of that prefix does the same job in place. Same
   * retained set either way; `sample()` below is unaffected. Even if an
   * out-of-order snapshot ever did arrive, the only consequence is that one
   * aged entry lingers a little longer: `sample()` picks by `serverTime`
   * comparison, never by buffer position, so a stale entry can't win over a
   * newer one.
   */
  pushSnapshot(snapshot: Snapshot): void {
    this.buffer.push(snapshot);
    const cutoff = snapshot.serverTime - 2000;
    let expired = 0;
    while (expired < this.buffer.length && this.buffer[expired].serverTime < cutoff) expired++;
    if (expired > 0) this.buffer.splice(0, expired);
  }

  /** Returns the interpolated pose to render given the current estimated server time, or null with no data yet. */
  sample(estimatedServerTimeNow: number): { x: number; z: number; facing: number } | null {
    const renderTime = estimatedServerTimeNow - interpDelayMs();
    let before: Snapshot | null = null;
    let after: Snapshot | null = null;

    for (const snap of this.buffer) {
      if (snap.serverTime <= renderTime && (!before || snap.serverTime > before.serverTime)) before = snap;
      if (snap.serverTime >= renderTime && (!after || snap.serverTime < after.serverTime)) after = snap;
    }

    if (before && after && before.serverTime !== after.serverTime) {
      const t = (renderTime - before.serverTime) / (after.serverTime - before.serverTime);
      return {
        x: before.x + (after.x - before.x) * t,
        z: before.z + (after.z - before.z) * t,
        // Shortest-arc, not scalar — see shortestAngleDelta above.
        facing: before.facing + shortestAngleDelta(before.facing, after.facing) * t,
      };
    }
    return before ?? after ?? null;
  }
}
